import Image from 'next/image';

import { Box, Flex, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import {
  type BackToSchool2026Phase2Variant,
  BTS_2026_P2_VARIANTS,
  getBts2026Phase2CheckoutHref,
} from '~/features/promotions/constants/back-to-school-2026-phase-2-sale';

import { BackToSchool2026Phase2EndsSoon } from './back-to-school-2026-phase-2-ends-soon';

export type BackToSchool2026Phase2PromoCardProps = {
  variant: BackToSchool2026Phase2Variant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
};

/**
 * Homepage impact carousel card for Back to School 2026 phase 2 (Figma 1944:1243 G1 desktop,
 * 1944:1282 G1 mobile, 1944:1262 G2 desktop, 1944:1301 G2 mobile). The urgency row moves per
 * variant: G1 sits it beside the CTA, G2 above the headline. Mobile stacks the row above the
 * full-width button in both cases.
 */
export function BackToSchool2026Phase2PromoCard({
  variant,
  promoCode,
  imagePriority,
  onClick,
}: BackToSchool2026Phase2PromoCardProps) {
  const config = BTS_2026_P2_VARIANTS[variant];
  const checkoutHref = getBts2026Phase2CheckoutHref(variant, promoCode);
  const cardId = `back-to-school-2026-phase-2-${variant}-sale-title`;

  const endsSoonRow = (
    <BackToSchool2026Phase2EndsSoon
      color={config.homepageEyebrowColor}
      label={config.homepageEyebrowLabel}
      showRule={config.homepageEyebrowPlacement === 'above-headline'}
    />
  );

  return (
    <PromoCard
      backgroundColor={config.homepageBackgroundColor}
      // PromoCard falls back to its default star eyebrow when this slot is nullish, so the variants
      // that render the row beside the CTA pass an empty fragment rather than null to suppress it.
      eyebrowSlot={config.homepageEyebrowPlacement === 'above-headline' ? endsSoonRow : <></>}
      id={cardId}
      sectionGap={6}
      backgroundImageSlot={
        <>
          <Box
            bottom={0}
            display={{ base: 'block', md: 'none' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              sizes='100vw'
              src={config.homepageBgMobile}
              style={{
                objectFit: config.homepageMobileObjectFit,
                objectPosition: config.homepageMobileObjectPosition,
              }}
              fill
            />
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              // The card renders ~1056px wide, and `cover` scales the art to ~1296px before
              // cropping, so the hint has to cover the overscan or the browser picks a
              // candidate too small and upscales it.
              // The art is an opaque dark gradient, where the default quality of 75 leaves
              // visible WebP blocking; 90 clears it and still serves ~22KB.
              quality={90}
              sizes='(max-width: 1120px) 100vw, 1296px'
              src={config.homepageBg}
              style={{ objectFit: config.homepageObjectFit, objectPosition: config.homepageObjectPosition }}
              fill
            />
          </Box>
        </>
      }
      buttonSlot={
        <Flex
          alignItems={{ base: 'stretch', md: 'center' }}
          flexDirection={{ base: 'column-reverse', md: 'row' }}
          gap={{ base: 4, md: 6 }}
          position='relative'
          zIndex={1}
        >
          <ButtonLink
            linkProps={{ href: checkoutHref }}
            buttonProps={{
              _hover: { opacity: 0.9 },
              alignItems: 'center',
              backgroundColor: config.homepageCtaColor,
              color: config.homepageCtaTextColor,
              display: 'inline-flex',
              fontSize: '15px',
              fontWeight: '400',
              height: 'auto',
              justifyContent: 'center',
              lineHeight: '17px',
              paddingX: '24px',
              paddingY: '14px',
              variant: 'unstyled',
              width: {
                base: '100%',
                md: '152px',
              },
              onClick,
            }}
          >
            {config.homepageCtaLabel}
          </ButtonLink>
          {config.homepageEyebrowPlacement === 'beside-cta' ? endsSoonRow : null}
        </Flex>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color={config.homepageTitleColor}
            fontWeight='600'
            id={cardId}
            letterSpacing='-0.01em'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.15',
              md: '1.1',
            }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              <Box as='span' color={config.homepageHeadlineLine1Color}>
                {config.homepageHeadlineLine1}
              </Box>
              {config.homepageHeadlineLine2 ? (
                <>
                  <br />
                  {config.homepageHeadlineLine2}
                </>
              ) : null}
            </LinkOverlay>
          </Heading>
          <Text color={config.homepageSubtitleColor} lineHeight={1.5} maxWidth={{ base: '200px', md: 'none' }}>
            {config.homepageSubtitle}
          </Text>
        </Box>
      }
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
